import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface SeoPage {
  title: string;
  eyebrow: string;
  description: string;
  sections: { title: string; text: string }[];
}

export const SEO_COMMUNITY_PAGES: Record<string, SeoPage> = {
  "emerald-chat-alternative": {
    eyebrow: "DISCOVER A NEW OPTION",
    title: "An Emerald Chat alternative",
    description:
      "Try a straightforward random chat experience built around shared interests, guest access, and respectful conversations.",
    sections: [
      {
        title: `Interest-aware matching`,
        text: `Like Emerald Chat, ${APP_NAME} uses interests to improve match quality. Unlike it, there is no account, karma system, or public profile.`,
      },
      {
        title: `Guest access only`,
        text: "Pick a nickname and start. No email, no social login, no profile to build or maintain.",
      },
      {
        title: "Practical safety tools",
        text: "Block and skip are always one tap away. Session-scoped exclusions mean blocked users stay blocked for the duration of your session.",
      },
    ],
  },
  "monkey-app-alternative": {
    eyebrow: "MEET PEOPLE ONLINE",
    title: "A Monkey app alternative",
    description:
      "Connect with new people without the noise of public feeds. Start a conversation and move on when it is no longer a fit.",
    sections: [
      {
        title: "Private by design",
        text: "No followers, no public feed, no performance. Just a one-to-one conversation that stays between the two of you.",
      },
      {
        title: `No app download`,
        text: `${APP_NAME} runs in your browser. No app store, no installation, no permissions beyond what the chat needs.`,
      },
      {
        title: `Move on easily`,
        text: "Every conversation is one skip away from a new one. There is no social obligation to stay.",
      },
    ],
  },
  "talk-to-strangers-online": {
    eyebrow: "START A CONVERSATION",
    title: `Talk to strangers online — free, safe, anonymous text chat`,
    description: `Talk to strangers online for free on ${APP_NAME}. Anonymous text chat with interest-based matching, no signup, and simple safety controls in every conversation.`,
    sections: [
      {
        title: `Real conversations, not just reactions`,
        text: `${APP_NAME} is built for people who want a genuine back-and-forth with someone they have never met — not a swipe, a like, or a reaction to a post. The format is simple: one-on-one text chat, a shared interest or two to break the ice, and the freedom to go wherever the conversation leads.`,
      },
      {
        title: `Better matches through shared interests`,
        text: `Meeting a complete stranger is easier when you already have something in common. Add interests like music, travel, books, fitness, or anything you enjoy, and ${APP_NAME} will prioritise matches who share them. The algorithm is a starting point, not a guarantee — part of the fun is discovering what you have in common after you start talking.`,
      },
      {
        title: `No commitment, no pressure — leave any time`,
        text: "You never owe a stranger a reply or an explanation. Use Skip to move on to the next match instantly, Block to keep someone out of your conversations, or simply close the tab when you are done. There is no social graph, no followers, and no obligation to stay.",
      },
    ],
  },
  about: {
    eyebrow: `ABOUT ${APP_NAME.toUpperCase()}`,
    title: `A better way to meet online`,
    description: `${APP_NAME} is a guest-first chat community for people who want simple, human conversations with new people around the world.`,
    sections: [
      {
        title: `Why we built ${APP_NAME}`,
        text: `Most social platforms are built around profiles, follower counts, and public performance. ${APP_NAME} is the opposite — a private, spontaneous conversation with someone new.`,
      },
      {
        title: `Interest-based matching`,
        text: "Shared interests make conversations easier. We use them to prioritise matches, so you always have a natural opening line.",
      },
      {
        title: "Safety first",
        text: "Block, skip, and report tools are built into every conversation. The community guidelines set a clear standard for respectful chat.",
      },
    ],
  },
  "safety-tips": {
    eyebrow: "SAFETY FIRST",
    title: "Safety tips for chatting online",
    description:
      "Protect your privacy, trust your instincts, and use the block and report tools whenever a conversation stops feeling right.",
    sections: [
      {
        title: "Keep personal details private",
        text: "Do not share your address, passwords, financial information, or private contact details with a stranger.",
      },
      {
        title: "Leave when you want",
        text: "You never owe anyone a reply. Skip, block, or report a user and move on.",
      },
      {
        title: "Report harmful behavior",
        text: `Use the in-chat controls to flag harassment, threats, sexual content, or anything that violates community guidelines.`,
      },
    ],
  },
  blog: {
    eyebrow: `THE ${APP_NAME.toUpperCase()} JOURNAL`,
    title: `Ideas for better conversations`,
    description:
      "Practical guides for meeting people online, staying safe, and making random conversations more meaningful.",
    sections: [
      {
        title: "Chat guides",
        text: "Learn how to start conversations, find common ground, and make the most of random chat.",
      },
      {
        title: "Safety advice",
        text: "Practical tips for protecting your privacy and staying safe when talking to strangers online.",
      },
      {
        title: "Platform comparisons",
        text: `Honest breakdowns of random chat platforms to help you find the one that suits you best.`,
      },
    ],
  },
};
